import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";

// Interfaces
import { ICitActivity } from "@/Common/types/ContractsInTransit/model";
import { CustomGridTreeDataGroupingCellProps } from "@/Layouts/components/types";
import { IWriteOffPreviewFilter } from "../../CitExport/interfaces";

// MUI
import { Stack, SxProps, Typography } from "@mui/material";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import {
	GridColDef,
	GridGroupingColDefOverride,
	GridRenderCellParams,
	GridRowSelectionModel,
	GridValidRowModel,
	useGridApiRef,
} from "@mui/x-data-grid-premium";

// Layouts
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import { CustomGridTreeDataGroupingCell } from "@/Layouts/components/TreeGrid/CustomGridTreeDataGroupingCell";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import TypographyCell from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";

// Helpers
import { generateSx, getRowClassName, getRowId } from "@/Layouts/components/Tables/DataSmartTable/helpers";
import { formatNumber, getCurrencySymbol } from "@/Common/helpers/number";
import { getFormatUTCDate } from "@/Common/helpers/Dates";
import { getFundedStatus, isParentActivity } from "@DataPages/helpers/financeManagement/cit/citTable";
import { getFormattedDate } from "@dealerwave/dw-layer-date-converter";

// Icons
import { Coin } from "../../Coin";
import ExportedIcon from "../../ExportedIcon";

// Constants
import { RECOMMENDED_ACTIONS } from "../../CitExport/helpers";
import { DEFAULT_HEADER_HEIGHT } from "@/Washouts/constants";
import { SHOW_AS_COLUMN, TAILWIND_FOR_DATA_SMART_TABLE } from "@/Constants/tables";
import { DATE_FORMAT } from "@/Constants/date";
import { NOT_APPLICABLE } from "@/DataPages/constants/cit";

const WIDTH_NUMBERS = {
	TWENTY_FIVE: 25,
	THIRTY_EIGHT: 38,
	FIFTY: 50,
	FIFTY_FIVE: 55,
	SIXTY_FIVE: 65,
	SEVENTY_FIVE: 75,
	EIGHTY_FIVE: 85,
	NINETY_FIVE: 95,
	HUNDRED_AND_TWENTY_FIVE: 125,
	HUNDRED_AND_THIRTY: 130,
};

const COLUMN_RESIZE_FACTOR = 2;
const HEADER_CUSTOM_CLASS_NAME = `table-header ${SHOW_AS_COLUMN} fs-12`;

const getExportedTooltipText = (activity: ICitActivity) => {
	const { exported } = activity ?? {};
	const { exportedOn, exportedBy, balance } = exported ?? {};

	const exportedOnFormatted = getFormattedDate(exportedOn, "", DATE_FORMAT.abbrMonthDayTime);

	const balanceFormatted = formatNumber(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, balance);
	const formattedBalance = `${getCurrencySymbol()}${balanceFormatted}`;

	return (
		<Typography key={String(exportedOnFormatted)} className="!font-regular-sans !font-normal !text-xs" noWrap>{`Exported ${String(
			exportedOnFormatted
		)} by ${exportedBy} (${formattedBalance})`}</Typography>
	);
};

/**
 * Workaround for grid rendering issue - **see bugfix/RTC-16179 for details**
 *
 * Forces a scroll refresh on DataGridPremium by temporarily adjusting the scroll position.
 */
const forceScrollRefresh = (apiRef: React.MutableRefObject<GridApiPremium>) => {
	const tableRef = apiRef.current;
	if (!tableRef) {
		return;
	}
	const scrollTop = tableRef.getScrollPosition().top;
	tableRef.scroll({ top: scrollTop + 1 });
	tableRef.scroll({ top: scrollTop });
};

const exportedTooltipOptions = {
	tooltip: {
		sx: {
			maxWidth: "none",
		},
	},
};

const slots = {
	toolbar: CommonToolbar,
	noRowsOverlay: NoRowsOverlay,
	noResultsOverlay: NoRowsOverlay,
};

const columnVisibilityModel = {
	_id: false,
	recommendedAction: false,
	isExportable: false,
};

interface ICitActivitiesTableProps {
	title: string;
	hierarchy: string;
	filter?: IWriteOffPreviewFilter;
	activities: ICitActivity[];
	selectedActivities?: string[];
	disabled?: boolean;
	isShowing?: boolean;
	onUpdateAction: (value: string[]) => void;
}

const getRecommendedActionSortValue = (activity: ICitActivity) => {
	const { recommendedAction = "", exported } = activity ?? {};

	if (recommendedAction === RECOMMENDED_ACTIONS.CLEARING && !exported) {
		return "A";
	}
	if (recommendedAction === RECOMMENDED_ACTIONS.REVIEWING && !exported) {
		return "B";
	}
	if (exported) {
		return "C";
	}
	return "D";
};

const dateFormatter = (value: string | number | Date) => getFormatUTCDate(value, DATE_FORMAT.usDateSlash);
const valueGetterDate = (value?: string) => value && moment(value).utc().format(DATE_FORMAT.usDateSlash);

export default function CitActivitiesTable(props: Readonly<ICitActivitiesTableProps>) {
	const {
		title,
		filter,
		hierarchy,
		isShowing,
		activities = [],
		selectedActivities = [],
		disabled = false,
		onUpdateAction = () => {},
	} = props ?? {};

	const apiRef = useGridApiRef();
	const isFirstRender = useRef(true);

	// States
	const [sx, setSx] = useState<SxProps>({});
	const [selectedRows, setSelectedRows] = useState<string[]>([]);

	const columns: GridColDef[] = [
		{ field: "_id", headerName: "ID", width: WIDTH_NUMBERS.FIFTY, filterable: false },
		{
			field: "recommendedAction",
			headerName: "",
			width: WIDTH_NUMBERS.FIFTY,
			filterable: false,
			sortable: true,
			valueGetter: (_, activity: ICitActivity) => getRecommendedActionSortValue(activity),
		},
		{
			field: "isExportable",
			headerName: "",
			width: WIDTH_NUMBERS.FIFTY,
			filterable: false,
			sortable: true,
		},
		{
			field: "exported",
			headerName: "",
			renderHeader: () => <></>,
			width: WIDTH_NUMBERS.THIRTY_EIGHT,
			maxWidth: WIDTH_NUMBERS.THIRTY_EIGHT,
			minWidth: WIDTH_NUMBERS.THIRTY_EIGHT,
			sortable: false,
			align: "center",
			type: "singleSelect",
			valueOptions: ["Exported", "Unexported"],
			valueGetter: (_value, activity: ICitActivity) => (!!activity?.exported ? "Exported" : "Unexported"),
			renderCell: (params) => {
				const { value } = params;

				const citActivity = params.row as ICitActivity;
				const isParent = isParentActivity(citActivity, hierarchy);

				if (value === "Unexported" || !isParent) {
					return <></>;
				}

				const tooltipText = getExportedTooltipText(citActivity);

				return (
					<Stack sx={{ marginTop: "-1px" }}>
						<ExportedIcon title={tooltipText} tooltipProps={{ placement: "right", slotProps: exportedTooltipOptions }} />
					</Stack>
				);
			},
		},
		{
			field: "funded",
			headerName: "",
			renderHeader: () => <></>,
			width: WIDTH_NUMBERS.THIRTY_EIGHT,
			maxWidth: WIDTH_NUMBERS.THIRTY_EIGHT,
			minWidth: WIDTH_NUMBERS.THIRTY_EIGHT,
			sortable: false,
			align: "center",
			type: "singleSelect",
			valueOptions: ["Funded", "Unfunded", NOT_APPLICABLE],
			valueGetter: (_value, activity: ICitActivity) => getFundedStatus(activity, hierarchy),
			renderCell: (params) => {
				const { value } = params;
				if (value === NOT_APPLICABLE) {
					return <></>;
				}
				return (
					<Stack sx={{ marginTop: "-1px" }}>
						<Coin active={value === "Funded"} activeText="Funded" inactiveText="Unfunded" />
					</Stack>
				);
			},
		},
		{
			field: "transactionDate",
			headerName: "Date",
			headerClassName: HEADER_CUSTOM_CLASS_NAME,
			width: WIDTH_NUMBERS.SEVENTY_FIVE,
			minWidth: WIDTH_NUMBERS.SEVENTY_FIVE,
			type: "date",
			sortable: true,
			valueGetter: valueGetterDate,
			valueFormatter: dateFormatter,
			renderCell: (params) => <TypographyCell params={{ ...params, formattedValue: params.formattedValue }} />,
		},
		{
			field: "control",
			headerName: "Control",
			headerClassName: HEADER_CUSTOM_CLASS_NAME,
			width: WIDTH_NUMBERS.SEVENTY_FIVE,
			minWidth: WIDTH_NUMBERS.SEVENTY_FIVE,
			maxWidth: WIDTH_NUMBERS.SEVENTY_FIVE * COLUMN_RESIZE_FACTOR,
			resizable: true,
			sortable: true,
			renderCell: (params) => <TypographyCell params={{ ...params }} />,
		},
		{
			field: "account",
			headerName: "Acct.",
			headerClassName: HEADER_CUSTOM_CLASS_NAME,
			width: WIDTH_NUMBERS.FIFTY_FIVE,
			minWidth: WIDTH_NUMBERS.FIFTY_FIVE,
			maxWidth: WIDTH_NUMBERS.FIFTY_FIVE * COLUMN_RESIZE_FACTOR,
			sortable: true,
			align: "right",
			renderCell: (params) => <TypographyCell params={{ ...params }} />,
		},
		{
			field: "description",
			headerName: "Description",
			headerClassName: HEADER_CUSTOM_CLASS_NAME,
			width: WIDTH_NUMBERS.NINETY_FIVE,
			minWidth: WIDTH_NUMBERS.NINETY_FIVE,
			maxWidth: WIDTH_NUMBERS.NINETY_FIVE * COLUMN_RESIZE_FACTOR,
			resizable: true,
			sortable: true,
			renderCell: (params) => <TypographyCell params={{ ...params }} />,
		},
		{
			field: "dealNumber",
			headerName: "Reference",
			headerClassName: HEADER_CUSTOM_CLASS_NAME,
			width: WIDTH_NUMBERS.EIGHTY_FIVE,
			minWidth: WIDTH_NUMBERS.EIGHTY_FIVE,
			maxWidth: WIDTH_NUMBERS.EIGHTY_FIVE * COLUMN_RESIZE_FACTOR,
			resizable: true,
			sortable: true,
			align: "right",
			renderCell: (params) => <TypographyCell params={{ ...params }} />,
		},
		{
			field: "age",
			type: "number",
			headerName: "Days",
			headerClassName: HEADER_CUSTOM_CLASS_NAME,
			width: WIDTH_NUMBERS.SIXTY_FIVE,
			minWidth: WIDTH_NUMBERS.SIXTY_FIVE,
			maxWidth: WIDTH_NUMBERS.SIXTY_FIVE * COLUMN_RESIZE_FACTOR,
			resizable: true,
			sortable: true,
			renderCell: (params) => <TypographyCell params={{ ...params }} />,
		},
		{
			field: "vinNumber",
			headerName: "VIN",
			headerClassName: HEADER_CUSTOM_CLASS_NAME,
			width: WIDTH_NUMBERS.EIGHTY_FIVE,
			minWidth: WIDTH_NUMBERS.EIGHTY_FIVE,
			maxWidth: WIDTH_NUMBERS.EIGHTY_FIVE * COLUMN_RESIZE_FACTOR,
			resizable: true,
			sortable: true,
			renderCell: (params) => <TypographyCell params={{ ...params }} />,
		},
		{
			field: "balance",
			headerName: "Balance",
			type: "number",
			headerClassName: HEADER_CUSTOM_CLASS_NAME,
			width: WIDTH_NUMBERS.NINETY_FIVE,
			minWidth: WIDTH_NUMBERS.NINETY_FIVE,
			maxWidth: WIDTH_NUMBERS.NINETY_FIVE * COLUMN_RESIZE_FACTOR,
			resizable: true,
			sortable: true,
			renderCell: (params: GridRenderCellParams) => (
				<TypographyCell params={params} render={"absolute"} hints={{ isMoney: true, noFocus: true }} />
			),
		},
		{
			field: "source",
			headerName: "Source",
			headerClassName: HEADER_CUSTOM_CLASS_NAME,
			width: WIDTH_NUMBERS.EIGHTY_FIVE,
			minWidth: WIDTH_NUMBERS.EIGHTY_FIVE,
			maxWidth: WIDTH_NUMBERS.EIGHTY_FIVE * COLUMN_RESIZE_FACTOR,
			resizable: true,
			sortable: true,
			type: "number",
			renderCell: (params) => <TypographyCell params={{ ...params }} />,
		},
		{
			field: "postingDate",
			headerName: "Posting Date",
			headerClassName: HEADER_CUSTOM_CLASS_NAME,
			width: WIDTH_NUMBERS.HUNDRED_AND_THIRTY,
			minWidth: WIDTH_NUMBERS.HUNDRED_AND_THIRTY,
			maxWidth: WIDTH_NUMBERS.HUNDRED_AND_THIRTY * COLUMN_RESIZE_FACTOR,
			resizable: true,
			sortable: true,
			type: "date",
			valueGetter: valueGetterDate,
			valueFormatter: dateFormatter,
			renderCell: (params) => <TypographyCell params={{ ...params, formattedValue: params.formattedValue }} />,
		},
		{
			field: "customerNumber",
			headerName: "Customer #",
			headerClassName: HEADER_CUSTOM_CLASS_NAME,
			width: WIDTH_NUMBERS.HUNDRED_AND_TWENTY_FIVE,
			minWidth: WIDTH_NUMBERS.HUNDRED_AND_TWENTY_FIVE,
			maxWidth: WIDTH_NUMBERS.HUNDRED_AND_TWENTY_FIVE * COLUMN_RESIZE_FACTOR,
			resizable: true,
			sortable: true,
			renderCell: (params) => <TypographyCell params={{ ...params }} />,
		},
	];

	const groupingColDef: GridGroupingColDefOverride = {
		width: WIDTH_NUMBERS.TWENTY_FIVE,
		maxWidth: WIDTH_NUMBERS.TWENTY_FIVE,
		minWidth: WIDTH_NUMBERS.TWENTY_FIVE,
		resizable: false,
		headerName: "Group",
		cellClassName: "[&_]:!p-0",
		renderHeader: () => <Stack alignItems="center" justifyContent="center" className="relative w-7 h-9"></Stack>,
		colSpan: (value) => (!value ? 2 : 1),
		renderCell: (params) => {
			return (
				<Stack className="pt-[2px]">
					<CustomGridTreeDataGroupingCell
						className={"size-[20px] relative !-top-[2px]"}
						{...(params as CustomGridTreeDataGroupingCellProps)}
					/>
				</Stack>
			);
		},
		getApplyQuickFilterFn: () => null,
	};

	const getTreeDataPath = useCallback((row: GridValidRowModel) => row?.[hierarchy] as string[], []);

	useEffect(() => {
		if (!apiRef.current?.subscribeEvent) {
			return () => {};
		}
	}, [activities]);

	useEffect(() => {
		if (isFirstRender.current) {
			const filteredActivities = activities.filter((activity) => {
				return (
					activity.recommendedAction === RECOMMENDED_ACTIONS.CLEARING &&
					isParentActivity(activity, hierarchy) &&
					!activity.exported
				);
			});
			const ids = filteredActivities.map((activity) => activity._id);
			setSelectedRows(ids);
			onUpdateAction(ids);
			isFirstRender.current = false;
		}
	}, [selectedActivities]);

	useEffect(() => {
		if (isShowing) {
			forceScrollRefresh(apiRef);
		}
	}, [apiRef, isShowing]);

	const slotProps = useMemo(
		() => ({
			toolbar: {
				filter: {
					hide: false,
				},
				search: {
					useQuickFilter: true,
				},
			},
			noRowsOverlay: {
				title: `No ${title} to show`,
				className: "!text-montserrat",
			},
			noResultsOverlay: {
				title: `No ${title} to show`,
				className: "!text-montserrat",
			},
			basePopper: {
				sx: { zIndex: 1300 },
			},
		}),
		[title]
	);

	const handleRowSelected = (rowSelectionModel: GridRowSelectionModel) => {
		if (disabled) {
			return;
		}
		const selected = rowSelectionModel as string[];
		setSelectedRows(selected);
		onUpdateAction(selected);
	};

	useEffect(() => {
		setSx(generateSx(apiRef));
		return apiRef.current?.subscribeEvent("virtualScrollerContentSizeChange", () => {
			setSx(generateSx(apiRef));
		});
	}, [props, apiRef]);

	const shouldDisableSelectAllCheckbox = Boolean(disabled || filter?.exportEntryIds?.length);

	return (
		<DataSmartTable
			apiRef={apiRef}
			autoHeight={false}
			columns={columns}
			rows={activities}
			disableChildrenFiltering
			disableColumnMenu
			disableRowSelectionOnClick
			getRowClassName={(params) => {
				const generalClasses = getRowClassName(params);
				const { recommendedAction = "", exported } = params.row as ICitActivity;
				const isParent = isParentActivity(params.row as ICitActivity, hierarchy);
				return clsx(generalClasses, {
					"[&_.MuiDataGrid-cellCheckbox]:shadow-[inset_4px_0px_0px_0px_var(--success-color)]":
						recommendedAction === RECOMMENDED_ACTIONS.CLEARING && !exported && isParent,
					"[&_.MuiDataGrid-cellCheckbox]:shadow-[inset_4px_0px_0px_0px_var(--cell-dark-yellow-color)]":
						recommendedAction === RECOMMENDED_ACTIONS.REVIEWING && !exported && isParent,
					"[&_.MuiDataGrid-cellCheckbox_.MuiButtonBase-root.MuiCheckbox-root]:!invisible": !isParent,
				});
			}}
			getRowId={getRowId}
			columnVisibilityModel={columnVisibilityModel}
			getTreeDataPath={getTreeDataPath}
			groupingColDef={groupingColDef}
			headerHeight={DEFAULT_HEADER_HEIGHT}
			checkboxSelection
			isRowSelectable={(params) => {
				const { row } = params;
				const isExportable = (row as ICitActivity).isExportable;
				return !disabled && isExportable;
			}}
			onRowSelectionModelChange={handleRowSelected}
			rowSelectionModel={selectedRows}
			hideFooter
			rowHeight={25}
			sx={sx}
			treeData
			initialState={{
				sorting: {
					sortModel: [
						{ field: "recommendedAction", sort: "asc" },
						{ field: "isExportable", sort: "desc" },
					],
				},
			}}
			customProps={{
				tableContainerClassName: "!h-full",
				tableClassName: {
					"functional-table display-select-all-checkbox": true,
					"[&_.MuiDataGrid-columnHeader_.MuiCheckbox-root]:!mr-[10px]": true,
					"[&_.MuiDataGrid-columnHeader_.MuiCheckbox-root]:ml-[4px]": true,
					"[&_.MuiDataGrid-columnHeader_.MuiCheckbox-root>.MuiTouchRipple-root]:!invisible": true,
					"[&_.MuiDataGrid-columnHeader_.MuiCheckbox-root]:text-disabled": shouldDisableSelectAllCheckbox,
					"[&_.MuiDataGrid-columnHeader_.MuiCheckbox-root]:!cursor-default": shouldDisableSelectAllCheckbox,
					"[&_.MuiDataGrid-columnHeader_.MuiCheckbox-root]:hover:!bg-transparent": shouldDisableSelectAllCheckbox,
					...TAILWIND_FOR_DATA_SMART_TABLE,
				},
				hierarchyKey: hierarchy,
				expandableRows: false,
				hide: false,
			}}
			slotProps={slotProps}
			slots={slots}
			enableCustomScrollbars
			maxRows={10}
		/>
	);
}
